import { Badge } from "@devhop/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  BanknoteIcon,
  CalendarIcon,
  ClipboardListIcon,
  FileTextIcon,
  StarIcon,
  WalletIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  dashboardKpiGridWideClassName,
  dashboardPageStackClassName,
  dashboardQueueGridClassName,
} from "@/modules/dashboard/presentation/ui/dashboard-card-styles";
import { DashboardStatCard } from "@/modules/dashboard/presentation/ui/DashboardStatCard";
import { PermissionGate } from "@/modules/auth/presentation/ui/PermissionGate";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { usePerformanceOperationsDashboardQuery } from "../api/queries";
import { PerformanceDashboardAnalyticsStrip } from "../ui/PerformanceDashboardAnalyticsStrip";
import {
  PerformanceDashboardSectionCard,
  performanceQueueItemClassName,
} from "../ui/PerformanceDashboardSectionCard";
import { PerformanceOperationsOverviewSection } from "../ui/PerformanceOperationsOverviewSection";
import {
  formatCurrencyBucketsInline,
  formatPerformanceDate,
  formatPerformanceMoney,
} from "../ui/PerformanceReportsNav";

const REQUEST_STATUS_LABELS: Record<string, string> = {
  new: "ຮັບຄຳຂໍໃໝ່",
  negotiating: "ກຳລັງເຈລະຈາ",
  pending_quote: "ລໍອອກໃບສະເໜີລາຄາ",
  won: "ຢືນຢັນແລ້ວ",
  lost: "ບໍ່ໄດ້ຮັບຈ້າງ",
  cancelled: "ຍົກເລີກ",
};

const EVENT_STATUS_LABELS: Record<string, string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນແລ້ວ",
  completed: "ສຳເລັດ",
  cancelled: "ຍົກເລີກ",
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  quotation: "ໃບສະເໜີລາຄາ",
  invoice: "ໃບເກັບເງິນ",
  receipt: "ໃບຮັບເງິນ",
};

const DOCUMENT_STATUS_LABELS: Record<string, string> = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  issued: "ອອກແລ້ວ",
  paid: "ຊຳລະແລ້ວ",
  voided: "ຍົກເລີກ",
};

const PAYOUT_STATUS_LABELS: Record<string, string> = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
};

export function PerformanceOperationsDashboardPage() {
  const nav = useNavigate();
  const { data, isLoading } = usePerformanceOperationsDashboardQuery();
  const kpis = data?.kpis;

  return (
    <>
      <Header>
        <Breadcrumb items={[{ label: "ແຜງຄວບຄຸມງານສະແດງ" }]} />
      </Header>

      <Main>
        <div className={dashboardPageStackClassName}>
          <PerformanceOperationsOverviewSection />

          <PermissionGate all={["performance-reports:read"]}>
            <PerformanceDashboardAnalyticsStrip />
          </PermissionGate>

          <div className={dashboardKpiGridWideClassName}>
            <PermissionGate all={["performance-requests:read"]}>
              <DashboardStatCard
                title="ຄຳຂໍຄ້າງດຳເນີນການ"
                tone="attendance"
                icon={ClipboardListIcon}
                isLoading={isLoading}
                value={(kpis?.openRequests ?? 0).toLocaleString()}
                onClick={() => void nav({ to: "/app/performance/requests" })}
              />
            </PermissionGate>
            <PermissionGate all={["performance-requests:read"]}>
              <DashboardStatCard
                title="ຄຳຂໍຄ້າງ > 7 ວັນ"
                tone="refunds"
                icon={ClipboardListIcon}
                isLoading={isLoading}
                value={(kpis?.openRequestsOverdue ?? 0).toLocaleString()}
                onClick={() =>
                  void nav({ to: "/app/performance/reports/requests" })
                }
              />
            </PermissionGate>
            <PermissionGate all={["performance-requests:read"]}>
              <DashboardStatCard
                title="ລໍຖ້າແປງເປັນງານ"
                tone="students"
                icon={ClipboardListIcon}
                isLoading={isLoading}
                value={(kpis?.wonAwaitingConvert ?? 0).toLocaleString()}
                onClick={() => void nav({ to: "/app/performance/requests" })}
              />
            </PermissionGate>
            <PermissionGate all={["performance-events:read"]}>
              <DashboardStatCard
                title="ງານໃນ 7 ວັນ"
                tone="packages"
                icon={CalendarIcon}
                isLoading={isLoading}
                value={(kpis?.upcomingEvents ?? 0).toLocaleString()}
                onClick={() => void nav({ to: "/app/performance/events" })}
              />
            </PermissionGate>
            <PermissionGate all={["performance-documents:read"]}>
              <DashboardStatCard
                title="ເອກະສານລໍ action"
                tone="attendance"
                icon={FileTextIcon}
                isLoading={isLoading}
                value={(kpis?.documentsPendingAction ?? 0).toLocaleString()}
                onClick={() => void nav({ to: "/app/performance/documents" })}
              />
            </PermissionGate>
            <PermissionGate all={["performance-payouts:read"]}>
              <DashboardStatCard
                title="ໃບເບີກຈ່າຍລໍ action"
                tone="packages"
                icon={WalletIcon}
                isLoading={isLoading}
                value={(kpis?.payoutsPendingAction ?? 0).toLocaleString()}
                onClick={() => void nav({ to: "/app/performance/payouts" })}
              />
            </PermissionGate>
            <PermissionGate all={["performance-reports:read"]}>
              <DashboardStatCard
                title="ຄ້າງເກັບ / ຄ້າງຈ່າຍ"
                tone="revenue"
                icon={BanknoteIcon}
                isLoading={isLoading}
                className="col-span-2 lg:col-span-2"
                value={
                  <span className="text-xl sm:text-2xl">
                    {formatCurrencyBucketsInline(kpis?.outstandingAr, {
                      showEmpty: true,
                    })}
                  </span>
                }
                onClick={() =>
                  void nav({ to: "/app/performance/reports/revenue" })
                }
              />
            </PermissionGate>
          </div>

          <div className={dashboardQueueGridClassName}>
            <PermissionGate all={["performance-requests:read"]}>
              <PerformanceDashboardSectionCard
                title="ຄຳຂໍຂອງຂ້ອຍ"
                viewAllTo="/app/performance/requests"
                isLoading={isLoading}
                isEmpty={!data?.myRequests?.length}
              >
                <ul className="flex flex-col gap-2">
                  {data?.myRequests?.map((row) => (
                    <li key={row.id}>
                      <Link
                        to="/app/performance/requests/$id"
                        params={{ id: row.id }}
                        className={performanceQueueItemClassName}
                      >
                        <div className="min-w-0 space-y-1">
                          <p className="truncate font-medium text-sm">
                            {row.title}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            ຮັບ {formatPerformanceDate(row.createdAt)}
                          </p>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          {REQUEST_STATUS_LABELS[row.status] ?? row.status}
                        </Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              </PerformanceDashboardSectionCard>
            </PermissionGate>

            <PermissionGate all={["performance-events:read"]}>
              <PerformanceDashboardSectionCard
                title={
                  <span className="inline-flex items-center gap-2">
                    <StarIcon className="size-4 text-primary" aria-hidden />
                    ງານໃນ 7 ວັນຂ້າງໜ້າ
                  </span>
                }
                viewAllTo="/app/performance/events"
                isLoading={isLoading}
                isEmpty={!data?.upcomingEvents?.length}
              >
                <ul className="flex flex-col gap-2">
                  {data?.upcomingEvents?.map((row) => (
                    <li key={row.id}>
                      <Link
                        to="/app/performance/events/$id"
                        params={{ id: row.id }}
                        className={performanceQueueItemClassName}
                      >
                        <div className="min-w-0 space-y-1">
                          <p className="truncate font-medium text-sm">
                            {row.title}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {formatPerformanceDate(row.eventDate)}
                            {row.locationText ? ` · ${row.locationText}` : ""}
                          </p>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          {EVENT_STATUS_LABELS[row.status] ?? row.status}
                        </Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              </PerformanceDashboardSectionCard>
            </PermissionGate>

            <PermissionGate all={["performance-documents:read"]}>
              <PerformanceDashboardSectionCard
                title="ເອກະສານລໍດຳເນີນການ"
                viewAllTo="/app/performance/documents"
                isLoading={isLoading}
                isEmpty={!data?.pendingDocuments?.length}
              >
                <ul className="flex flex-col gap-2">
                  {data?.pendingDocuments?.map((row) => (
                    <li key={row.id}>
                      <div className={performanceQueueItemClassName}>
                        <div className="min-w-0 space-y-1">
                          <p className="font-medium text-sm tabular-nums">
                            {row.documentNo}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {DOCUMENT_TYPE_LABELS[row.documentType] ??
                              row.documentType}
                            {" · "}
                            <Link
                              to="/app/performance/events/$id"
                              params={{ id: row.eventId }}
                              className="text-primary hover:underline"
                            >
                              {row.eventTitle ?? row.eventId}
                            </Link>
                          </p>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          {DOCUMENT_STATUS_LABELS[row.status] ?? row.status}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              </PerformanceDashboardSectionCard>
            </PermissionGate>

            <PermissionGate all={["performance-payouts:read"]}>
              <PerformanceDashboardSectionCard
                title="ໃບເບີກຈ່າຍລໍດຳເນີນການ"
                viewAllTo="/app/performance/payouts"
                isLoading={isLoading}
                isEmpty={!data?.pendingPayouts?.length}
              >
                <ul className="flex flex-col gap-2">
                  {data?.pendingPayouts?.map((row) => (
                    <li key={row.id}>
                      <Link
                        to="/app/performance/payouts/$id"
                        params={{ id: row.id }}
                        className={performanceQueueItemClassName}
                      >
                        <div className="min-w-0 space-y-1">
                          <p className="font-medium text-sm tabular-nums">
                            {row.payoutNo}
                          </p>
                          <p className="truncate text-muted-foreground text-xs">
                            {row.performerName ?? "—"}
                            {row.eventTitle ? ` · ${row.eventTitle}` : ""}
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1 sm:items-end">
                          <span className="font-semibold text-sm tabular-nums">
                            {formatPerformanceMoney(row.totalAmount)}
                          </span>
                          <Badge variant="outline" className="font-normal">
                            {PAYOUT_STATUS_LABELS[row.status] ?? row.status}
                          </Badge>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </PerformanceDashboardSectionCard>
            </PermissionGate>
          </div>
        </div>
      </Main>
    </>
  );
}
